export type TelemetryFailureReport = (label: string, error: unknown) => void;

export function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : String(error);
}

// describeError is different from errorMessage for two inputs. It reads the
// message of an object that is not an Error, and it gives 'Unknown error' for
// null or undefined.
export function describeError(error: unknown): string {
  if (error instanceof Error) {
    return error.message;
  }
  if (error && typeof error === 'object' && 'message' in error) {
    return String(error.message);
  }
  return String(error ?? 'Unknown error');
}

// messageOf is different from errorMessage for two inputs. It gives the
// message of an object that is not an Error, and it gives `undefined` for a
// value that has no message. It does not change the type of the message.
export function messageOf(error: unknown): unknown {
  if (error && typeof error === 'object' && 'message' in error) {
    return error.message;
  }
  return undefined;
}

export function warnTelemetryFailure(label: string, error: unknown): void {
  console.warn(label, errorMessage(error));
}

export function warnMessageOf(label: string, error: unknown): void {
  // biome-ignore lint/suspicious/noConsole: preserve kiosk support diagnostics.
  console.warn(label, messageOf(error));
}

// A telemetry call must not stop the kiosk flow. A caller with its own console
// format gives its own report, thus its console output stays the same.
export function track(
  label: string,
  action: () => void,
  report: TelemetryFailureReport = warnTelemetryFailure
): void {
  try {
    action();
  } catch (error) {
    report(label, error);
  }
}

export async function trackAsync(
  label: string,
  action: () => Promise<void>
): Promise<void> {
  try {
    await action();
  } catch (error) {
    warnTelemetryFailure(label, error);
  }
}

export function silently(action: () => void): void {
  try {
    action();
  } catch {
    return;
  }
}
